import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import * as classesService from "../services/classes";
import * as lessonsService from "../services/lessons";
import * as studentsService from "../services/students";
import "./Lessons.css";

function toLocalInputValue(isoString) {
  if (!isoString) {
    return "";
  }
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

function localToIso(localValue) {
  if (!localValue) {
    return null;
  }
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
}

function defaultScheduledAt() {
  const upcoming = new Date(Date.now() + 60 * 60 * 1000);
  return toLocalInputValue(upcoming.toISOString());
}

const INITIAL_FORM = {
  title: "",
  description: "",
  scheduled_at: defaultScheduledAt(),
  duration_minutes: "60",
  status: "scheduled",
  student_input: "",
  teacher_id: "",
  allow_parent_join_meeting: true,
  image_url: "",
};

export default function LessonEditor() {
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const { user, accessToken } = useAuth();
  const { t, language } = useTranslation();
  const dir = language === "ar" ? "rtl" : "ltr";

  const isEdit = Boolean(lessonId);
  const canManage = Boolean(user && ["admin", "teacher"].includes(user.role));

  const [form, setForm] = useState(INITIAL_FORM);
  const [lesson, setLesson] = useState(null);
  const [students, setStudents] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const statusOptions = useMemo(
    () => [
      { value: "scheduled", label: t("lessonEditor.status.scheduled") },
      { value: "completed", label: t("lessonEditor.status.completed") },
      { value: "cancelled", label: t("lessonEditor.status.cancelled") },
    ],
    [t],
  );

  useEffect(() => {
    if (!accessToken || !user) {
      setLoading(false);
      return;
    }

    let ignore = false;

    async function loadStudents() {
      try {
        if (user.role === "admin" || user.role === "parent") {
          const [payload, classPayload] = await Promise.all([
            studentsService.fetchStudents({ accessToken }),
            user.role === "admin"
              ? classesService
                  .fetchClasses({ accessToken })
                  .catch(() => ({ classes: [] }))
              : Promise.resolve({ classes: [] }),
          ]);
          if (!ignore) {
            setStudents(payload.students || []);
            if (user.role === "admin") {
              setClassGroups(classPayload.classes || []);
            }
          }
        } else if (user.role === "teacher") {
          const [lessonsPayload, classPayload] = await Promise.all([
            lessonsService.fetchLessons({ accessToken }).catch(() => ({
              lessons: [],
            })),
            classesService
              .fetchClasses({ accessToken })
              .catch(() => ({ classes: [] })),
          ]);
          if (!ignore) {
            const seen = new Set();
            const uniqueStudents = [];

            const classes = classPayload.classes || [];
            classes.forEach((group) => {
              (group?.students || []).forEach((student) => {
                if (student?.id && !seen.has(student.id)) {
                  seen.add(student.id);
                  uniqueStudents.push(student);
                }
              });
            });

            if (!uniqueStudents.length) {
              (lessonsPayload.lessons || []).forEach((lessonItem) => {
                const student = lessonItem.student;
                if (student?.id && !seen.has(student.id)) {
                  seen.add(student.id);
                  uniqueStudents.push(student);
                }
              });
            }

            setStudents(uniqueStudents);
            setClassGroups(classes);
          }
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError.message);
        }
      } finally {
        if (!ignore && !isEdit) {
          setLoading(false);
        }
      }
    }

    loadStudents();
    return () => {
      ignore = true;
    };
  }, [accessToken, isEdit, user]);

  const selectedClass = useMemo(() => {
    if (!selectedClassId) return null;
    return (
      (classGroups || []).find(
        (g) => String(g.id) === String(selectedClassId),
      ) || null
    );
  }, [classGroups, selectedClassId]);

  const availableStudents = useMemo(() => {
    if (!selectedClass) {
      return students;
    }
    const classStudents = selectedClass.students || [];
    if (!classStudents.length) {
      return [];
    }
    return classStudents;
  }, [students, selectedClass]);

  const classStudentsLabel = useMemo(() => {
    if (!selectedClass) return "";
    const fromObjects = Array.isArray(selectedClass.students)
      ? selectedClass.students
          .map((s) => s?.full_name)
          .filter(Boolean)
          .join(", ")
      : "";
    if (fromObjects) return fromObjects;

    const ids = Array.isArray(selectedClass.student_ids)
      ? selectedClass.student_ids.map((id) => String(id))
      : [];
    if (!ids.length) return "";

    const byId = new Map((students || []).map((s) => [String(s.id), s]));
    const names = ids
      .map((id) => byId.get(String(id))?.full_name)
      .filter(Boolean);
    return names.join(", ");
  }, [selectedClass, students]);

  useEffect(() => {
    if (user?.role !== "teacher" && user?.role !== "admin") return;
    if (!selectedClass) return;
    if (!form.student_input) return;
    const allowedIds = new Set(
      [
        ...(selectedClass.student_ids || []),
        ...(Array.isArray(selectedClass.students)
          ? selectedClass.students.map((s) => s?.id).filter(Boolean)
          : []),
      ].map((id) => String(id)),
    );
    if (!allowedIds.has(String(form.student_input))) {
      setForm((current) => ({ ...current, student_input: "" }));
    }
  }, [selectedClassId, selectedClass, user?.role, form.student_input]);

  useEffect(() => {
    if (!isEdit || !lessonId || !accessToken) {
      setLoading(false);
      return;
    }

    let ignore = false;

    async function loadLesson() {
      setLoading(true);
      try {
        const payload = await lessonsService.fetchLesson({
          accessToken,
          lessonId,
        });
        if (ignore) {
          return;
        }
        setLesson(payload);
        setSelectedClassId(
          payload?.class_group?.id ? String(payload.class_group.id) : "",
        );
        setForm({
          title: payload.title || "",
          description: payload.description || "",
          scheduled_at: toLocalInputValue(payload.scheduled_at),
          duration_minutes: String(payload.duration_minutes || "60"),
          status: payload.status || "scheduled",
          student_input: payload.student?.id ? String(payload.student.id) : "",
          teacher_id: payload.teacher?.id ? String(payload.teacher.id) : "",
          allow_parent_join_meeting:
            payload.allow_parent_join_meeting === false ? false : true,
          image_url: payload.image_url || "",
        });
        // ensure current student appears in options
        setStudents((existing) => {
          if (!payload.student) {
            return existing;
          }
          const already = existing.some(
            (student) => student.id === payload.student.id,
          );
          if (already) {
            return existing;
          }
          return [...existing, payload.student];
        });
      } catch (loadError) {
        if (!ignore) {
          setError(loadError.message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadLesson();
    return () => {
      ignore = true;
    };
  }, [isEdit, lessonId, accessToken]);

  const handleChange = (event) => {
    const { name, type, value, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!accessToken) {
      return;
    }

    const trimmedTitle = form.title.trim();
    if (!trimmedTitle) {
      setSubmitError(t("lessonEditor.errors.title"));
      return;
    }

    const scheduledISO = localToIso(form.scheduled_at);
    if (!scheduledISO) {
      setSubmitError(t("lessonEditor.errors.schedule"));
      return;
    }

    const durationValue = Number(form.duration_minutes);
    if (!Number.isFinite(durationValue) || durationValue <= 0) {
      setSubmitError(t("lessonEditor.errors.duration"));
      return;
    }

    const payload = {
      title: trimmedTitle,
      description: form.description.trim(),
      scheduled_at: scheduledISO,
      duration_minutes: durationValue,
      status: form.status,
      image_url: form.image_url.trim(),
    };

    if (user?.role === "admin") {
      const teacherId = Number(form.teacher_id);
      if (Number.isInteger(teacherId) && teacherId > 0) {
        payload.teacher_id = teacherId;
      } else {
        setSubmitError(t("lessonEditor.errors.teacher"));
        return;
      }

      payload.allow_parent_join_meeting = Boolean(form.allow_parent_join_meeting);
    }

    setSubmitError(null);
    setSaving(true);
    try {
      let result;
      if (isEdit) {
        const studentId = Number(form.student_input);
        if (!Number.isInteger(studentId) || studentId <= 0) {
          setSubmitError(t("lessonEditor.errors.student"));
          setSaving(false);
          return;
        }
        payload.student_id = studentId;
        const targetLessonId = lesson?.id || Number(lessonId);
        result = await lessonsService.updateLesson(
          accessToken,
          targetLessonId,
          payload,
        );
      } else if (selectedClass) {
        const classStudentIds = (selectedClass.student_ids || [])
          .map((id) => Number(id))
          .filter((n) => Number.isInteger(n) && n > 0);
        if (!classStudentIds.length) {
          setSubmitError(t("lessonEditor.errors.noStudentsInClass"));
          setSaving(false);
          return;
        }

        for (const sid of classStudentIds) {
          await lessonsService.createLesson(accessToken, {
            ...payload,
            student_id: sid,
            class_group_id: selectedClass.id,
          });
        }
        navigate("/lessons");
        return;
      } else {
        const studentId = Number(form.student_input);
        if (!Number.isInteger(studentId) || studentId <= 0) {
          setSubmitError(t("lessonEditor.errors.student"));
          setSaving(false);
          return;
        }
        payload.student_id = studentId;
        result = await lessonsService.createLesson(accessToken, payload);
      }
      navigate(`/lessons/${result.id}`);
    } catch (submitErr) {
      setSubmitError(submitErr.message);
    } finally {
      setSaving(false);
    }
  };

  if (user && !canManage) {
    return (
      <div className="lessons-page" dir={dir}>
        <div className="lessons-shell">
          <div className="lessons-error" role="alert">
            <p>{t("lessonEditor.accessDenied")}</p>
            <Link
              to="/lessons"
              className="lessons-action lessons-action--ghost"
            >
              {t("lessonEditor.backToList")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="lessons-page" dir={dir}>
        <div className="lessons-shell">
          <p className="lessons-muted">{t("lessonEditor.loading")}</p>
        </div>
      </div>
    );
  }

  if (isEdit && error && !lesson) {
    return (
      <div className="lessons-page" dir={dir}>
        <div className="lessons-shell">
          <div className="lessons-error" role="alert">
            <p>
              {t("lessonEditor.loadError")}: {error}
            </p>
            <Link
              to="/lessons"
              className="lessons-action lessons-action--ghost"
            >
              {t("lessonEditor.backToList")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lessons-page" dir={dir}>
      <div className="lessons-shell lesson-editor">
        <header>
          <h1>
            {isEdit
              ? t("lessonEditor.title.edit")
              : t("lessonEditor.title.create")}
          </h1>
          <p className="lesson-note">
            {isEdit
              ? t("lessonEditor.note.edit")
              : t("lessonEditor.note.create")}
          </p>
        </header>

        {error && (
          <div className="lessons-error" role="alert">
            <p>
              {t("lessonEditor.genericError")}: {error}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="lesson-form">
          <div className="lesson-field">
            <label htmlFor="lesson-title">{t("lessonEditor.form.title")}</label>
            <input
              id="lesson-title"
              name="title"
              type="text"
              value={form.title}
              onChange={handleChange}
              placeholder={t("lessonEditor.placeholders.title")}
              disabled={saving}
            />
          </div>

          <div className="lesson-field">
            <label htmlFor="lesson-description">
              {t("lessonEditor.form.description")}
            </label>
            <textarea
              id="lesson-description"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder={t("lessonEditor.placeholders.description")}
              disabled={saving}
            />
          </div>

          <div className="lesson-field">
            <label htmlFor="lesson-scheduled">
              {t("lessonEditor.form.scheduledAt")}
            </label>
            <input
              id="lesson-scheduled"
              name="scheduled_at"
              type="datetime-local"
              value={form.scheduled_at}
              onChange={handleChange}
              required
              disabled={saving}
            />
            <p className="lesson-help">{t("lessonEditor.help.scheduledAt")}</p>
          </div>

          <div className="lesson-detail__grid">
            <div className="lesson-field">
              <label htmlFor="lesson-duration">
                {t("lessonEditor.form.duration")}
              </label>
              <input
                id="lesson-duration"
                name="duration_minutes"
                type="number"
                min="10"
                step="5"
                value={form.duration_minutes}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            <div className="lesson-field">
              <label htmlFor="lesson-status">
                {t("lessonEditor.form.status")}
              </label>
              <select
                id="lesson-status"
                name="status"
                value={form.status}
                onChange={handleChange}
                disabled={saving}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="lesson-field">
            {(user?.role === "teacher" || user?.role === "admin") &&
              (classGroups.length > 0 || (isEdit && lesson?.class_group)) && (
                <>
                  <label htmlFor="lesson-class">
                    {t("lessonEditor.form.classGroup")}
                  </label>
                  {classGroups.length > 0 ? (
                    <select
                      id="lesson-class"
                      value={selectedClassId}
                      onChange={(e) => setSelectedClassId(e.target.value)}
                      disabled={saving || isEdit}
                    >
                      <option value="">
                        {t("lessonEditor.form.allStudents")}
                      </option>
                      {classGroups.map((group) => (
                        <option key={group.id} value={String(group.id)}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id="lesson-class"
                      value={lesson?.class_group?.name || ""}
                      disabled
                    />
                  )}
                  <p className="lesson-help">
                    {t("lessonEditor.help.classCreatesLessons")}
                  </p>
                </>
              )}
          </div>

          <div className="lesson-field">
            <label htmlFor="lesson-student">
              {isEdit && lesson?.class_group
                ? t("lessonEditor.form.students")
                : t("lessonEditor.form.student")}
            </label>
            {isEdit && lesson?.class_group ? (
              <>
                <textarea
                  id="lesson-student"
                  value={classStudentsLabel || "—"}
                  disabled
                  rows={3}
                />
                <p className="lesson-help">
                  {t("lessonEditor.help.classStudents")}
                </p>
              </>
            ) : (
              <>
                <input
                  id="lesson-student"
                  name="student_input"
                  list="lesson-students"
                  value={form.student_input}
                  onChange={handleChange}
                  placeholder={t("lessonEditor.placeholders.student")}
                  required={isEdit || !selectedClassId}
                  disabled={saving || (!isEdit && Boolean(selectedClassId))}
                />
                {availableStudents.length > 0 && (
                  <datalist id="lesson-students">
                    {availableStudents.map((student) => (
                      <option key={student.id} value={student.id}>
                        {student.full_name}
                      </option>
                    ))}
                  </datalist>
                )}
                <p className="lesson-help">{t("lessonEditor.help.student")}</p>
              </>
            )}
          </div>

          {user?.role === "admin" && (
            <div className="lesson-field">
              <label htmlFor="lesson-allow-parent-join">
                {t("lessonEditor.form.allowParentJoinMeeting")}
              </label>
              <input
                id="lesson-allow-parent-join"
                name="allow_parent_join_meeting"
                type="checkbox"
                checked={Boolean(form.allow_parent_join_meeting)}
                onChange={handleChange}
                disabled={saving}
              />
              <p className="lesson-help">
                {t("lessonEditor.help.allowParentJoinMeeting")}
              </p>
            </div>
          )}

          {user?.role === "admin" && (
            <div className="lesson-field">
              <label htmlFor="lesson-teacher">
                {t("lessonEditor.form.teacher")}
              </label>
              <input
                id="lesson-teacher"
                name="teacher_id"
                type="number"
                min="1"
                value={form.teacher_id}
                onChange={handleChange}
                placeholder={t("lessonEditor.placeholders.teacher")}
                disabled={saving}
              />
              <p className="lesson-help">{t("lessonEditor.help.teacher")}</p>
            </div>
          )}

          <div className="lesson-field">
            <label htmlFor="lesson-image">{t("lessonEditor.form.image")}</label>
            <input
              id="lesson-image"
              name="image_url"
              type="url"
              value={form.image_url}
              onChange={handleChange}
              placeholder={t("lessonEditor.placeholders.image")}
              disabled={saving}
            />
          </div>

          {submitError && (
            <div className="lessons-error" role="alert">
              <p>{submitError}</p>
            </div>
          )}

          <div className="lesson-editor__actions">
            <button type="submit" className="btn primary" disabled={saving}>
              {saving
                ? t("lessonEditor.actions.saving")
                : isEdit
                  ? t("lessonEditor.actions.update")
                  : t("lessonEditor.actions.save")}
            </button>
            <Link
              to={isEdit ? `/lessons/${lessonId}` : "/lessons"}
              className="lessons-action lessons-action--ghost"
            >
              {t("lessonEditor.actions.cancel")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
